import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, Loader } from 'lucide-react';
import { toast } from 'sonner';
import { AuthContext } from '@/App';

const BACKEND_URL = window.__BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BillingSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { checkAuth } = React.useContext(AuthContext);
  const [status, setStatus] = useState('checking'); // checking, success, error
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) {
      toast.error('No session ID found');
      navigate('/billing');
      return;
    }

    pollPaymentStatus(sessionId);
  }, [searchParams, navigate]);

  const pollPaymentStatus = async (sessionId, attemptCount = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attemptCount >= maxAttempts) {
      setStatus('error');
      toast.error('Payment verification timed out');
      return;
    }

    setAttempts(attemptCount + 1);

    try {
      const response = await axios.get(
        `${API}/billing/status/${sessionId}`,
        { withCredentials: true }
      );

      if (response.data.payment_status === 'paid') {
        setStatus('success');
        toast.success('Payment successful! Your plan has been upgraded.');
        await checkAuth(); // Refresh user data
        setTimeout(() => navigate('/app/create'), 4000);
      } else if (response.data.status === 'expired') {
        setStatus('error');
        toast.error('Payment session expired');
      } else {
        // Continue polling
        setTimeout(() => pollPaymentStatus(sessionId, attemptCount + 1), pollInterval);
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('error');
      toast.error('Failed to verify payment');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#121417] p-4" data-testid="billing-success-page">
      <div className="glass-strong rounded-2xl p-12 text-center max-w-md">
        {status === 'checking' && (
          <>
            <Loader size={64} className="mx-auto mb-6 text-[#3A7BFF] animate-spin" />
            <h2 className="text-2xl font-bold text-white mb-3">Verifying Payment...</h2>
            <p className="text-[#8A93A6] mb-4">Please wait while we confirm your payment</p>
            <p className="text-sm text-[#8A93A6]">Attempt {attempts} of 5</p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle size={64} className="mx-auto mb-6 text-[#29E07F]" />
            <h2 className="text-2xl font-bold text-white mb-3">Payment Successful!</h2>
            <p className="text-[#8A93A6] mb-6">Your plan has been activated and you now have full access</p>
            
            {/* Clear expectations */}
            <div className="text-left bg-white/5 rounded-lg p-4 mb-6 space-y-3">
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-[#29E07F] mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-white">Access Granted Immediately</p>
                  <p className="text-xs text-[#8A93A6] mt-1">You can now use all features included in your plan</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-[#29E07F] mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-white">Execution Begins After Content Submission</p>
                  <p className="text-xs text-[#8A93A6] mt-1">Upload or create content to start automated workflows</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-[#29E07F] mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-white">Progress Visible Inside the App</p>
                  <p className="text-xs text-[#8A93A6] mt-1">Track all activity in CREATE and ANALYZE sections</p>
                </div>
              </div>
            </div>
            
            <p className="text-sm text-[#8A93A6]">Redirecting to app...</p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#FF4F4F]/20 flex items-center justify-center">
              <svg className="w-8 h-8 text-[#FF4F4F]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-white mb-3">Payment Verification Failed</h2>
            <p className="text-[#8A93A6] mb-6">We couldn't verify your payment. Please contact support.</p>
            <button
              onClick={() => navigate('/billing')}
              className="btn-primary"
              data-testid="back-to-billing-button"
            >
              Back to Billing
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default BillingSuccess;
